import { c as _c } from "react/compiler-runtime";
import { IcoClose16 } from '@onefootprint/icons';
// The design system's Dialog always paints its own header bar; this panel's heading is part of its content.
import * as RadixDialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { PopupModal } from 'react-calendly';
import { useTranslation } from 'react-i18next';
import useTrackingStorage from '@/hooks/use-tracking-storage';
import AccountNotFound from './account-not-found';
import DemoForm from './demo-form';
import DemoSent from './demo-sent';
import useRequestDemo, { type DemoRequest } from './use-request-demo';
const CALENDLY_URL = 'https://calendly.com/d/ct62-rtf-7xf/footprint-sales-demo';
type DemoDialogProps = {
  open: boolean;
  accountNotFound?: boolean;
  onClose: () => void;
};
const DemoDialog = t0 => {
  const $ = _c(44);
  const {
    open,
    onClose,
    accountNotFound: t1
  } = t0;
  const accountNotFound = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "demo"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("authentication", t2);
  const {
    data: tracking
  } = useTrackingStorage();
  const requestDemo = useRequestDemo();
  const {
    reset
  } = requestDemo;
  const [step, setStep] = useState(accountNotFound ? "account-not-found" : "form");
  const [booking, setBooking] = useState(false);
  let t3;
  let t4;
  if ($[1] !== accountNotFound || $[2] !== open || $[3] !== reset) {
    t3 = () => {
      if (open) {
        setStep(accountNotFound ? "account-not-found" : "form");
        reset();
      }
    };
    t4 = [open, reset, accountNotFound];
    $[1] = accountNotFound;
    $[2] = open;
    $[3] = reset;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== requestDemo || $[7] !== tracking) {
    t5 = request => {
      requestDemo.mutate({
        ...request,
        tracking
      }, {
        onSuccess: () => setStep("sent")
      });
    };
    $[6] = requestDemo;
    $[7] = tracking;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleSubmit = t5;
  let t6;
  if ($[9] !== onClose) {
    t6 = () => {
      onClose();
      setBooking(true);
    };
    $[9] = onClose;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleBook = t6;
  const submitted = requestDemo.variables;
  let t7;
  if ($[11] !== onClose) {
    t7 = isOpen => !isOpen && onClose();
    $[11] = onClose;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <RadixDialog.Overlay className="fixed inset-0 z-overlay bg-black/50 backdrop-blur-[2px] data-[state=closed]:animate-fadeOut data-[state=open]:animate-fadeIn" />;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t) {
    t9 = t(_temp);
    $[14] = t;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IcoClose16 />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t9) {
    t11 = <RadixDialog.Close aria-label={t9} className="absolute right-5 top-5 flex size-7 cursor-pointer items-center justify-center rounded-full bg-senary text-secondary hover:text-primary max-md:after:absolute max-md:after:-inset-2 max-md:sticky max-md:top-[max(1.25rem,env(safe-area-inset-top))] max-md:z-10 max-md:-mb-7 max-md:shrink-0 max-md:self-end">{t10}</RadixDialog.Close>;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== onClose || $[20] !== step) {
    t12 = step === "account-not-found" ? <AccountNotFound onRequestDemo={() => setStep("form")} onRetry={onClose} Title={RadixDialog.Title} Description={RadixDialog.Description} /> : null;
    $[19] = onClose;
    $[20] = step;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== handleBook || $[23] !== onClose || $[24] !== step) {
    t13 = step === "sent" ? <DemoSent onBook={handleBook} onDecline={onClose} Title={RadixDialog.Title} Description={RadixDialog.Description} /> : null;
    $[22] = handleBook;
    $[23] = onClose;
    $[24] = step;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== handleSubmit || $[27] !== requestDemo.isError || $[28] !== requestDemo.isPending || $[29] !== step) {
    t14 = step === "form" ? <DemoForm isPending={requestDemo.isPending} failed={requestDemo.isError} onSubmit={handleSubmit} Title={RadixDialog.Title} Description={RadixDialog.Description} /> : null;
    $[26] = handleSubmit;
    $[27] = requestDemo.isError;
    $[28] = requestDemo.isPending;
    $[29] = step;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== t13 || $[34] !== t14) {
    t15 = <RadixDialog.Portal>{t8}<RadixDialog.Content className="fp-auth-brand fixed inset-0 z-dialog m-auto flex h-fit max-h-[calc(100%-2rem)] w-[560px] max-w-[calc(100%-2rem)] flex-col overflow-auto rounded-2xl border border-tertiary bg-primary p-6 text-primary data-[state=closed]:animate-scaleOut data-[state=open]:animate-scaleIn sm:p-8 max-md:m-0 max-md:h-dvh max-md:max-h-none max-md:w-full max-md:max-w-none max-md:rounded-none max-md:border-0 max-md:pt-[max(1.5rem,env(safe-area-inset-top))] max-md:pb-[max(1.5rem,env(safe-area-inset-bottom))]">{t11}{t12}{t13}{t14}</RadixDialog.Content></RadixDialog.Portal>;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== booking || $[37] !== submitted) {
    t16 = booking && submitted ? <PopupModal url={CALENDLY_URL} open={true} onModalClose={() => setBooking(false)} rootElement={document.body} prefill={{
      email: submitted.email,
      firstName: submitted.firstName,
      lastName: submitted.lastName,
      name: `${submitted.firstName} ${submitted.lastName}`
    }} utm={{
      utmCampaign: submitted.tracking.utmCampaign,
      utmSource: submitted.tracking.utmSource,
      utmMedium: submitted.tracking.utmMedium,
      utmContent: submitted.tracking.utmContent,
      utmTerm: submitted.tracking.utmTerm
    }} /> : null;
    $[36] = booking;
    $[37] = submitted;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== open || $[40] !== t15 || $[41] !== t16 || $[42] !== t7) {
    t17 = <RadixDialog.Root open={open} onOpenChange={t7}>{t15}{t16}</RadixDialog.Root>;
    $[39] = open;
    $[40] = t15;
    $[41] = t16;
    $[42] = t7;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
};
export default DemoDialog;
function _temp($) {
  return $.close;
}